// src/features/landing/presentation/pages/JobListPage.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Footer } from "../components/Footer";
import { useJobPosts } from "../../application/useJobPosts";

import "../styles/landing.css";

// 커스텀 드롭다운 컴포넌트
function CustomSelect({ value, onChange, options, label, placeholder }) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
    };

    const displayValue = value || placeholder;

    return (
        <div className="filter-row">
            <label className="filter-label">{label}</label>
            <div className="custom-select" ref={dropdownRef}>
                <div className="select-trigger" onClick={() => setIsOpen(!isOpen)}>
                    <span className={`select-value ${!value ? 'placeholder' : ''}`}>
                        {displayValue}
                    </span>
                    <span className={`select-arrow ${isOpen ? 'open' : ''}`}>▼</span>
                </div>
                {isOpen && (
                    <div className="select-dropdown">
                        {options.map((option) => (
                            <div
                                key={option}
                                className={`select-option ${value === option ? 'selected' : ''}`}
                                onClick={() => handleSelect(option)}
                            >
                                {option}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

const REGIONS = ["전체", "서울특별시", "경기도", "인천광역시", "부산광역시", "대구광역시", "광주광역시", "대전광역시", "울산광역시", "세종특별자치시", "강원도", "충북남도", "충청남도", "전라북도", "전라남도", "경상북도", "경상남도", "제주특별자치시"];
// eslint-disable-next-line no-unused-vars
const JOB_TYPES = ["전체", "총무", "설계사", "TM설계사", "영업지원", "영업교육"];
const SORT_OPTIONS = [
    { value: "latest", label: "등록일순", field: "createdAt" },
    { value: "deadline", label: "마감일순", field: "applyEndDate" },
];

function getStatusDisplay(status) {
    switch (status) {
        case "OPEN":
            return "상시채용";
        case "COMPLETE":
            return "채용완료";
        case "CLOSED":
            return "채용마감";
        case "PENDING":
            return "대기중";
        default:
            return "상시채용";
    }
}

function getStatusClass(status) {
    switch (status) {
        case "COMPLETE":
            return "status-complete";
        case "CLOSED":
            return "status-closed";
        default:
            return "status-open";
    }
}

function getSalaryDisplay(min, max) {
    const isMinNegotiable = !min || min === "협의";
    const isMaxNegotiable = !max || max === "협의";

    if (isMinNegotiable && isMaxNegotiable) return "면접후결정";
    if (isMinNegotiable) return `${max} 이하`;
    if (isMaxNegotiable) return `${min} 이상`;
    if (min === max) return min;
    return `${min} ~ ${max}`;
}

export function JobListPage() {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(0);
    const [sortBy, setSortBy] = useState("latest");
    const [sortDirection, setSortDirection] = useState("desc");
    const [selectedRegion, setSelectedRegion] = useState("전체");
    // eslint-disable-next-line no-unused-vars
    const [selectedJobType, setSelectedJobType] = useState("전체");

    const { jobPosts, loading, error, totalPages, totalElements, setFilters, setPage } = useJobPosts(8, 0, { sort: "createdAt,desc" });

    const handleJobClick = (jobId) => {
        navigate(`/jobs/${jobId}`);
    };

    const buildFilters = (region, jobType, sortField, direction) => {
        const filters = {};
        if (region && region !== "전체") {
            filters.companySido = region;
        }
        if (jobType && jobType !== "전체") {
            filters.jobType = jobType;
        }
        const option = SORT_OPTIONS.find(o => o.value === sortField);
        if (option) {
            filters.sort = `${option.field},${direction}`;
        }
        return filters;
    };

    const handleSearch = () => {
        const filters = buildFilters(selectedRegion, selectedJobType, sortBy, sortDirection);
        setFilters(filters);
        setCurrentPage(0);
    };

    const handleSortChange = (newSortBy) => {
        let newDirection = sortDirection;
        if (sortBy === newSortBy) {
            newDirection = sortDirection === "desc" ? "asc" : "desc";
        } else {
            newDirection = "desc";
        }
        setSortBy(newSortBy);
        setSortDirection(newDirection);
        const filters = buildFilters(selectedRegion, selectedJobType, newSortBy, newDirection);
        setFilters(filters);
        setCurrentPage(0);
    };

    const handlePageChange = (page) => {
        setPage(page);
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(0, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(0, endPage - maxVisiblePages + 1);
        }

        // Previous button
        pages.push(
            <button
                key="prev"
                className="page-btn"
                onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
            >
                &lt;
            </button>
        );

        // First page
        if (startPage > 0) {
            pages.push(
                <button key={0} className="page-btn" onClick={() => handlePageChange(0)}>
                    1
                </button>
            );
            if (startPage > 1) {
                pages.push(<span key="dots1" className="page-dots">...</span>);
            }
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    className={`page-btn ${currentPage === i ? 'active' : ''}`}
                    onClick={() => handlePageChange(i)}
                >
                    {i + 1}
                </button>
            );
        }

        // Last page
        if (endPage < totalPages - 1) {
            if (endPage < totalPages - 2) {
                pages.push(<span key="dots2" className="page-dots">...</span>);
            }
            pages.push(
                <button key={totalPages - 1} className="page-btn" onClick={() => handlePageChange(totalPages - 1)}>
                    {totalPages}
                </button>
            );
        }

        // Next button
        pages.push(
            <button
                key="next"
                className="page-btn"
                onClick={() => handlePageChange(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage === totalPages - 1}
            >
                &gt;
            </button>
        );

        return pages;
    };

    return (
        <>
            <div style={{ paddingTop: '40px', minHeight: '100vh', background: '#f8f9fa' }}>
                {/* Search Filter Section */}
                <section className="job-search-section">
                    <div className="search-container">
                        <h2 className="search-title">채용정보검색</h2>
                        <div className="search-filters">
                            <CustomSelect
                                value={selectedRegion}
                                onChange={setSelectedRegion}
                                options={REGIONS}
                                placeholder="전체"
                            />
                            {/* 직무 검색 - 추후 활성화 예정
                            <CustomSelect
                                value={selectedJobType}
                                onChange={setSelectedJobType}
                                options={JOB_TYPES}
                                label="세부사항"
                                placeholder="전체"
                            />
                            */}
                            <button className="search-btn" onClick={handleSearch}>검 색</button>
                        </div>
                    </div>
                </section>

                {/* Job List Section */}
                <section className="job-list-section">
                    <div className="job-list-container">
                        <div className="verified-notice">
                            <span className="verified-icon">&#10003;</span>
                            {/*<span className="verified-text">현재, 라이징브로커즈의 검증을 거쳐 업데이트 된 회사입니다.</span><br />*/}
                            <span className="verified-text">본 공고는 라이징브로커즈의 검증을 거쳐 업데이트된 프리미엄 기업입니다.</span><br />
                            <span className="verified-text-1">채용공고 상세 페이지에서 지원하시면, 전담 매니저가 1:1로 지원 과정을 도와드립니다.</span>
                            {/*<span className="verified-text-1">지원 신청 시, 개별 맞춤 1:1 지원 서비스를 제공해 드립니다.</span>*/}
                        </div>
                        <div className="job-list-header">
                            <h3 className="job-list-title">
                                채용정보 <span className="job-count">{totalElements?.toLocaleString() || 0}</span> 건
                            </h3>
                            <div className="sort-options">
                                {SORT_OPTIONS.map((option, index) => (
                                    <React.Fragment key={option.value}>
                                        <button
                                            className={`sort-btn ${sortBy === option.value ? 'active' : ''}`}
                                            onClick={() => handleSortChange(option.value)}
                                        >
                                            {option.label}
                                            <span className={`sort-arrow ${sortBy === option.value ? (sortDirection === 'desc' ? 'desc' : 'asc') : ''}`}>
                                                {sortBy === option.value ? (sortDirection === 'desc' ? '▼' : '▲') : '▼'}
                                            </span>
                                        </button>
                                        {index < SORT_OPTIONS.length - 1 && <span className="sort-divider">|</span>}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading-container">
                                <div className="loading-spinner"></div>
                                <p>채용정보를 불러오는 중...</p>
                            </div>
                        ) : error ? (
                            <div className="notice-container">
                                <div className="notice-icon">📋</div>
                                <p className="notice-title">채용정보 준비 중</p>
                                <p className="notice-description">현재 채용정보를 준비하고 있습니다.<br/>잠시 후 다시 확인해 주세요.</p>
                            </div>
                        ) : (
                            <>
                                <div className="job-list">
                                    {jobPosts.map((post) => (
                                        <div key={post.id} className={`job-item${post.status === 'COMPLETE' ? ' job-item-complete' : ''}`} onClick={() => handleJobClick(post.id)}>
                                            {post.status === 'COMPLETE' && (
                                                <div className="complete-stamp">COMPLETED</div>
                                            )}
                                            <div className="job-header">
                                                {post.status === 'COMPLETE' && <div className="complete-check-icon">✓</div>}
                                                <h3 className="job-company-name">{post.companyName}</h3>
                                            </div>
                                            <div className="job-info">
                                                <div className="info-row">
                                                    <span className="info-label">직무</span>
                                                    <span className="info-value">{post.jobType || "채용"}</span>
                                                </div>
                                                <div className="info-row">
                                                    <span className="info-label">지역</span>
                                                    <span className="info-value">{[post.companySido, post.companySigungu].filter(Boolean).join(" ") || "전국"}</span>
                                                </div>
                                                <div className="info-row">
                                                    <span className="info-label">급여</span>
                                                    <span className="info-value">{getSalaryDisplay(post.salaryRangeMin, post.salaryRangeMax)}</span>
                                                </div>
                                            </div>
                                            <div className="job-footer">
                                                <span className={`job-status ${getStatusClass(post.status)}`}>
                                                    {getStatusDisplay(post.status)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="pagination">
                                        {renderPagination()}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </section>
            </div>
            <Footer />

            <style jsx>{`
                /* Search Section */
                .job-search-section {
                    background: white;
                    padding: 30px 40px;
                    position: relative;
                    z-index: 10;
                }

                .search-container {
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .search-title {
                    font-size: 1.3rem;
                    font-weight: 700;
                    color: #2c3e50;
                    margin: 0 0 20px 0;
                }

                .search-filters {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 20px;
                    align-items: flex-end;
                    position: relative;
                    z-index: 100;
                }

                .filter-row {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .filter-label {
                    font-size: 0.9rem;
                    color: #666;
                    font-weight: 500;
                }

                .filter-select {
                    padding: 10px 15px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 0.95rem;
                    min-width: 150px;
                    background: white;
                    cursor: pointer;
                }

                .search-btn {
                    padding: 12px 40px;
                    margin-bottom: 2px;
                    background: #666;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .search-btn:hover {
                    background: #555;
                }

                /* Custom Select Styles */
                .custom-select {
                    position: relative;
                    width: 100%;
                    min-width: 180px;
                }

                .select-trigger {
                    padding: 12px 15px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 0.95rem;
                    background: white;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    transition: all 0.2s ease;
                }

                .select-trigger:hover {
                    border-color: #f16102;
                }

                .select-value {
                    flex: 1;
                    color: #333;
                }

                .select-value.placeholder {
                    color: #999;
                }

                .select-arrow {
                    font-size: 0.65rem;
                    color: #666;
                    transition: transform 0.2s ease;
                }

                .select-arrow.open {
                    transform: rotate(180deg);
                }

                .select-dropdown {
                    position: absolute;
                    top: calc(100% + 4px);
                    left: 0;
                    right: 0;
                    background: white;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
                    z-index: 9999;
                    max-height: 260px;
                    overflow-y: auto;
                    animation: dropdownSlide 0.2s ease;
                }

                @keyframes dropdownSlide {
                    from {
                        opacity: 0;
                        transform: translateY(-8px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .select-dropdown::-webkit-scrollbar {
                    width: 6px;
                }

                .select-dropdown::-webkit-scrollbar-track {
                    background: #f5f5f5;
                    border-radius: 3px;
                }

                .select-dropdown::-webkit-scrollbar-thumb {
                    background: #ccc;
                    border-radius: 3px;
                }

                .select-dropdown::-webkit-scrollbar-thumb:hover {
                    background: #aaa;
                }

                .select-option {
                    padding: 12px 15px;
                    font-size: 0.9rem;
                    color: #333;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    border-bottom: 1px solid #f5f5f5;
                }

                .select-option:last-child {
                    border-bottom: none;
                }

                .select-option:hover {
                    background: #f8f9fa;
                    color: #f16102;
                }

                .select-option.selected {
                    background: #e8f0ff;
                    color: #f16102;
                    font-weight: 600;
                }

                /* Premium Section */
                .premium-section {
                    padding: 40px;
                    background: white;
                    margin-bottom: 20px;
                }

                .premium-container {
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .premium-title {
                    font-size: 1.2rem;
                    font-weight: 700;
                    color: #2c3e50;
                    margin: 0 0 25px 0;
                }

                .premium-cards {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 20px;
                }

                .premium-card {
                    background: white;
                    border: 1px solid #e9ecef;
                    border-radius: 12px;
                    padding: 32px 28px;
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    transition: all 0.3s ease;
                }

                .premium-card:not(.placeholder):hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
                    border-color: #f16102;
                    cursor: pointer;
                }

                .premium-card-header {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    gap: 8px;
                    padding-bottom: 16px;
                }

                .premium-card-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #2c3e50;
                    margin: 0;
                    line-height: 1.4;
                }

                .premium-card-company {
                    font-size: 0.9rem;
                    font-weight: 400;
                    color: #868e96;
                    margin: 0;
                }

                .premium-card-info {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    flex: 1;
                }

                .premium-info-row {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .premium-info-label {
                    font-size: 0.85rem;
                    color: #adb5bd;
                    font-weight: 500;
                    min-width: 32px;
                    padding-right: 12px
                }

                .premium-info-value {
                    text-align: left;
                    font-size: 0.95rem;
                    color: #343a40;
                    font-weight: 600;
                    flex: 1;
                }

                .premium-card-footer {
                    display: flex;
                    justify-content: flex-end;
                    padding-top: 16px;
                    border-top: 1px solid #e9ecef;
                }

                .premium-card-status {
                    font-size: 0.85rem;
                    font-weight: 600;
                    padding: 6px 14px;
                    border-radius: 6px;
                    background: #d3f9d8;
                    color: #2b8a3e;
                }

                .premium-card-status.closed {
                    background: #ffe8cc;
                    color: #e8590c;
                }

                .premium-card.placeholder {
                    border: 2px dashed #dee2e6;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 240px;
                }

                .premium-card.placeholder:hover {
                    transform: none;
                    box-shadow: none;
                    border-color: #dee2e6;
                }

                .placeholder-icon {
                    opacity: 0.4;
                    margin-bottom: 10px;
                }

                .placeholder-text {
                    font-size: 0.9rem;
                    color: #adb5bd;
                    margin: 0;
                    font-weight: 600;
                }

                /* Job List Section */
                .job-list-section {
                    padding: 40px;
                    background: white;
                }

                .job-list-container {
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .verified-notice {
                    align-items: center;
                    gap: 8px;
                    padding: 12px 16px;
                    background: #fff9eb;
                    border-radius: 8px;
                    margin-bottom: 24px;
                }

                .verified-icon {
                    margin: 7px;
                    align-items: center;
                    justify-content: center;
                    width: 20px;
                    height: 20px;
                    color: #f68716;
                    border-radius: 50%;
                    font-size: 0.9rem;
                    font-weight: 700;
                }

                .verified-text {
                    font-size: 1.0rem;
                    color: #f68716;
                    font-weight: 500;
                }

                .verified-text-1 {
                    font-size: 1.0rem;
                    color: #56320d;
                    font-weight: 500;
                }

                .job-list-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                    padding-bottom: 15px;
                    border-bottom: 2px solid #333;
                }

                .job-list-title {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #333;
                    margin: 0;
                }

                .job-count {
                    color: #f16102;
                    font-weight: 700;
                }

                .sort-options {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .sort-btn {
                    background: none;
                    border: none;
                    font-size: 0.9rem;
                    color: #888;
                    cursor: pointer;
                    padding: 5px 10px;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }

                .sort-btn.active {
                    color: #333;
                    font-weight: 600;
                }

                .sort-arrow {
                    font-size: 0.6rem;
                    color: #ccc;
                    transition: transform 0.2s ease;
                }

                .sort-btn.active .sort-arrow {
                    color: #f16102;
                }

                .sort-divider {
                    color: #ddd;
                }

                /* Job Items */
                .job-list {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 25px;
                }

                .job-item {
                    position: relative;
                    background: white;
                    border: 1px solid #e9ecef;
                    border-radius: 12px;
                    padding: 32px 28px;
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    transition: all 0.3s ease;
                    cursor: pointer;
                    overflow: hidden;
                }

                .complete-check-icon {
                    width: 27px;
                    height: 25px;
                    flex-shrink: 0;
                    border-radius: 50%;
                    border: 3px solid rgba(185, 28, 28, 0.8);
                    color: rgba(185, 28, 28, 0.8);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1rem;
                    font-weight: 900;
                    pointer-events: none;
                }

                .complete-stamp {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%) rotate(10deg);
                    border: 3px solid rgb(200 0 0 / 80%);
                    color: rgb(200 0 0 / 80%);
                    font-size: 1.25rem;
                    font-weight: 900;
                    letter-spacing: 3px;
                    padding: 5px 25px;
                    border-radius: 4px;
                    pointer-events: none;
                    white-space: nowrap;
                    z-index: 2;
                }

                .job-item:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
                    border-color: #f16102;
                }

                .job-header {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding-bottom: 16px;
                }

                .job-company-name {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #2c3e50;
                    margin: 0;
                    line-height: 1.4;
                }

                .job-info {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    flex: 1;
                }

                .info-row {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .info-label {
                    font-size: 0.85rem;
                    color: #adb5bd;
                    font-weight: 500;
                    min-width: 32px;
                }

                .info-value {
                    text-align: left;
                    font-size: 0.95rem;
                    color: #343a40;
                    font-weight: 600;
                    flex: 1;
                }

                .job-footer {
                    display: flex;
                    justify-content: flex-end;
                    padding-top: 16px;
                    border-top: 1px solid #e9ecef;
                }

                .job-status {
                    font-size: 0.85rem;
                    font-weight: 600;
                    padding: 6px 14px;
                    border-radius: 6px;
                }

                .status-open {
                    background: #d3f9d8;
                    color: #2b8a3e;
                }

                .status-closed {
                    background: #ffe8cc;
                    color: #e8590c;
                }

                .status-complete {
                    background: #e9ecef;
                    color: #495057;
                }

                .job-item-complete {
                    opacity: 0.72;
                    background: #f8f9fa;
                }

                .job-item-complete:hover {
                    border-color: #adb5bd;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.07);
                }


                /* Pagination */
                .pagination {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 5px;
                    margin-top: 40px;
                    padding: 20px 0;
                }

                .page-btn {
                    min-width: 36px;
                    height: 36px;
                    border: 1px solid #ddd;
                    background: white;
                    color: #666;
                    font-size: 0.9rem;
                    cursor: pointer;
                    border-radius: 4px;
                    transition: all 0.2s;
                }

                .page-btn:hover:not(:disabled) {
                    background: #f5f5f5;
                    border-color: #f16102;
                    color: #f16102;
                }

                .page-btn.active {
                    background: #f16102;
                    border-color: #f16102;
                    color: white;
                }

                .page-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .page-dots {
                    padding: 0 5px;
                    color: #999;
                }

                /* Loading & Notice */
                .loading-container {
                    text-align: center;
                    padding: 60px 0;
                    color: #666;
                }

                .notice-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 80px 20px;
                    text-align: center;
                }

                .notice-icon {
                    font-size: 3rem;
                    margin-bottom: 16px;
                }

                .notice-title {
                    font-size: 1.3rem;
                    font-weight: 600;
                    color: #495057;
                    margin: 0 0 12px 0;
                }

                .notice-description {
                    font-size: 1rem;
                    color: #868e96;
                    margin: 0;
                    line-height: 1.6;
                }

                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 3px solid #e9ecef;
                    border-top-color: #f16102;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 16px;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                /* Responsive */
                @media (max-width: 1200px) {
                    .job-list {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }

                @media (max-width: 900px) {
                    .premium-cards {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .job-list {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (max-width: 600px) {
                    .job-search-section,
                    .premium-section,
                    .job-list-section {
                        padding: 16px;
                    }

                    .search-title {
                        font-size: 1.1rem;
                        margin-bottom: 16px;
                    }

                    .search-filters {
                        flex-direction: column;
                        gap: 12px;
                    }

                    .filter-row {
                        width: 100%;
                    }

                    .filter-label {
                        font-size: 0.85rem;
                    }

                    .custom-select {
                        width: 100%;
                    }

                    .select-trigger {
                        padding: 12px;
                        font-size: 0.9rem;
                    }

                    .select-dropdown {
                        max-height: 220px;
                    }

                    .select-option {
                        padding: 11px 12px;
                        font-size: 0.85rem;
                    }

                    .search-btn {
                        width: 100%;
                        padding: 14px;
                        font-size: 0.95rem;
                    }

                    .premium-title {
                        font-size: 1rem;
                        margin-bottom: 16px;
                    }

                    .premium-cards {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }

                    .premium-card {
                        padding: 24px 20px;
                        gap: 16px;
                    }

                    .premium-card-header {
                        gap: 6px;
                        padding-bottom: 12px;
                    }

                    .premium-card-title {
                        font-size: 1.1rem;
                    }

                    .premium-card-company {
                        font-size: 0.85rem;
                    }

                    .premium-card-info {
                        gap: 10px;
                    }

                    .premium-info-label {
                        font-size: 0.8rem;
                        min-width: 28px;
                    }

                    .premium-info-value {
                        font-size: 0.9rem;
                    }

                    .premium-card-footer {
                        padding-top: 12px;
                    }

                    .premium-card-status {
                        font-size: 0.8rem;
                        padding: 5px 12px;
                    }

                    .premium-card.placeholder {
                        min-height: 180px;
                    }

                    .verified-notice {
                        padding: 10px 12px;
                        margin-bottom: 16px;
                    }

                    .verified-icon {
                        width: 18px;
                        height: 18px;
                        font-size: 0.65rem;
                    }

                    .verified-text {
                        font-size: 0.85rem;
                        line-height: 1.5;
                        margin-bottom: 4px;
                    }

                    .verified-text-1 {
                        font-size: 0.85rem;
                        line-height: 1.5;
                        margin-top: 6px;
                        padding: 0 12px;
                        display: inline-block;
                    }

                    .job-list-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                        padding-bottom: 12px;
                    }

                    .job-list-title {
                        font-size: 1rem;
                    }

                    .sort-options {
                        width: 100%;
                        justify-content: flex-start;
                    }

                    .sort-btn {
                        font-size: 0.85rem;
                        padding: 4px 8px;
                    }

                    .job-list {
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }

                    .job-item {
                        padding: 24px 20px;
                        gap: 16px;
                    }

                    .job-header {
                        gap: 6px;
                        padding-bottom: 12px;
                    }

                    .job-company-name {
                        font-size: 1.1rem;
                    }

                    .job-info {
                        gap: 10px;
                    }

                    .info-label {
                        font-size: 0.8rem;
                        min-width: 28px;
                    }

                    .info-value {
                        font-size: 0.9rem;
                    }

                    .job-footer {
                        padding-top: 12px;
                    }

                    .job-status {
                        font-size: 0.8rem;
                        padding: 5px 12px;
                    }

                    .pagination {
                        margin-top: 24px;
                        padding: 16px 0;
                        gap: 4px;
                    }

                    .page-btn {
                        min-width: 32px;
                        height: 32px;
                        font-size: 0.85rem;
                    }
                }
            `}</style>
        </>
    );
}
